import * as d3 from 'd3';

/**
 * Generates a box plot chart.
 * 
 * @param {string} container_id - The ID of the container element where the chart will be appended.
 * @param {number[]} array - The array of numbers used to generate the box plot.
 * @returns {SVGElement} - The SVG element representing the box plot chart.
 */
export function gen_box(container_id, array) {
    // set the dimensions and margins of the graph
    let margin = {top: 10, right: 30, bottom: 10, left: 30};
    const width = 200 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Compute summary statistics used for the box:
    let arr_sorted = array.sort(d3.ascending)
    let q1 = d3.quantile(arr_sorted, .25)
    let median = d3.quantile(arr_sorted, .5)
    let q3 = d3.quantile(arr_sorted, .75)
    let interQuantileRange = q3 - q1
    let min = Math.max(0, q1 - 1.5 * interQuantileRange)
    let max = q3 + 1.5 * interQuantileRange
    
    // Show the Y scale
    let y = d3.scaleLinear()
        // .domain([0, d3.max([max, d3.max(array)])])
        .domain([0, max+1])
        .nice()
        .range([height, 0]);

    // a few features for the box
    let box_center = 100-15
    let box_width = width

    // append the svg object to the body of the page
    let svg = d3.select(`#${container_id}`)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        // .call(zoom)
        .append("g")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Add the Y gridlines
    svg.append("g")
        .attr("class", "y-grid")
        .call(d3.axisLeft(y).tickSizeInner(-width-margin.left).tickSizeOuter(0).tickFormat(''))
        .selectAll("line")
        .attr("stroke", "gray")
        .attr("stroke-dasharray", "6")
        .call(g => g.select(".domain").remove());

    // Add the Y axis with numbers
    svg.append("g")
        .attr("class", "y-axis")
        .attr("color", "gray")
        .call(d3.axisLeft(y))
        .call(g => g.select(".domain").remove());

    // Show the main vertical line
    let mainLine = svg
        .append("line")
        .attr("x1", box_center)
        .attr("x2", box_center)
        .attr("y1", y(min) )
        .attr("y2", y(max) )
        .attr("stroke", "#fdab13")
        .attr("stroke-width", "2px")

    // Show the box
    let box = svg
        .append("rect")
        .attr("x", box_center - width/2)
        .attr("y", y(q3) )
        .attr("height", (y(q1)-y(q3)) )
        .attr("width", box_width )
        .style("fill", "rgb(241,230,108)")
        .style("fill-opacity", "0.4")
        .attr("stroke", "#fdab13")
        .attr("stroke-width", "2px")

    // show median, min and max horizontal lines
    let lines = svg
        .selectAll("toto")
        .data([min, max])
        .enter()
        .append("line")
        .attr("x1", box_center-70/2)
        .attr("x2", box_center+70/2)
        .attr("y1", function(d){ return(y(d))} )
        .attr("y2", function(d){ return(y(d))} )
        .attr("stroke", "#fdab13")
        .attr("stroke-width", "2px")

    let median_lines = svg
        .selectAll("toto")
        .data([median])
        .enter()
        .append("line")
        .attr("x1", box_center-width/2)
        .attr("x2", box_center+width/2)
        .attr("y1", function(d){ return(y(d))} )
        .attr("y2", function(d){ return(y(d))} )
        .attr("stroke", "rgb(0,194,165)")
        .attr("stroke-width", "3.4px")


    svg.select(".y-grid").select(".domain").remove();

    return svg.node();

    function zoom(svg) {
        const extent = [[0, 0], [width, height]];

        svg.call(d3.zoom()
            .scaleExtent([1, 15])
            .translateExtent(extent)
            .extent(extent)
            .on("zoom", zoomed));

        function zoomed(event) {

            let newY = event.transform.rescaleY(y);

            svg.selectAll(".y-axis").call(d3.axisLeft(newY));
            svg.selectAll(".y-grid").call(d3.axisLeft(newY).tickSizeInner(-width-margin.left).tickSizeOuter(0).tickFormat(''))
                .selectAll("line")
                .attr("stroke", "gray")
                .attr("stroke-dasharray", "4")

            svg.select(".y-axis").select(".domain").remove();
            svg.select(".y-grid").select(".domain").remove();

            mainLine
                .attr("y1", newY(min))
                .attr("y2", newY(max));

            box
                .attr("y", newY(q3))
                .attr("height", (newY(q1) - newY(q3)));

            lines
                .attr("y1", d => newY(d))
                .attr("y2", d => newY(d));

            median_lines
                .attr("y1", d => newY(d))
                .attr("y2", d => newY(d));
        }
    }
}

/**
 * Updates or creates a box plot using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {Array} array - The data array used to generate the box plot.
 * @returns {SVGElement} - The generated or updated SVG element containing the box plot.
 */
export function update_box(container_id, array) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return gen_box(container_id, array);
    }
    // Update 
    else {
        svg.remove();
        return gen_box(container_id, array);
    }
}